import React, { FC, useCallback, useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { api } from '../api/api'
import SubInfo from '../components/Sub/SubInfo'
import { useActions } from '../hooks/useAction'
import { useTypedSelector } from '../hooks/useTypedSelector'
import Notification from '../models/notification'
import { Sub } from '../types/subscription/sub'
import { EResponseCode, Subscription, SubscriptionBuyRequest, SubscriptionBuyResponse } from '../types/subscription/subscription'
import { ESubscription, User } from '../types/user/user'
import styles from './css/SubPage.module.css'
import { AxiosError } from 'axios'
import DropdownMenu from '../components/Sub/SelectPeriod'

const SubPage: FC = () => {
	const [active, setActive] = useState(false)
	const [currentSub, setCurrentSub] = useState(0)
	const [subs, setSubs] = useState<Sub[] | null>(null)
	const [selectedSubs, setSelectedSubs] = useState<{ [key: number]: Sub | null }>({});

	const { user, userInfo } = useTypedSelector(x => x.user)
	const {
		current: currentLang,
		lang: { Value: lang },
	} = useTypedSelector(x => x.lang)

	const { setLoadingScreen, showConfirmation, setUser } = useActions()

	useEffect(() => {
		document.title = 'vvTable - Sub'

		const fetchApi = async () => {
			setLoadingScreen(true)

			await getSubs()

			setLoadingScreen(false)
		}

		fetchApi()
	}, [])

	const handleSetActiveInfo = (currIndex: number) => {
		setCurrentSub(currIndex)
		setActive(true)
	}

	const getSubs = async () => {
		try {
			const response = await api.get<Subscription[]>('subscription/list')
			const _subs = response.data
			const newSubs: Sub[] = _subs.map(sub => {
				return {
					sub: sub.access,
					name: User.GetSubscriptionShort(sub.access),
					price: sub.price,
					discount: sub.discount,
					period: sub.period
				} as Sub
			})
			
			setSubs(newSubs)
		} catch {
			Notification.ShowNotification(lang.Notification_Error)
		}
	}

	
	const handleSubChange = useCallback((index: number, sub: Sub) => {
		setSelectedSubs((prev) => ({ ...prev, [index]: sub }));
	  }, []);

	const afterBought = async () => {
		await getSubs()
	}

	const buySub = async (sub: Sub) => {
		showConfirmation({
			text: `${lang.Confirmation_BuySub}.\n${{ 0: 'Выбранный период' }[currentLang] || 'Selected period'}: ${sub.period} ${{ 0: 'дней' }[currentLang] || 'days'} - ${sub.period >= 30 ? sub.price - sub.discount : sub.price} ${{ 0: 'ключей' }[currentLang] || 'keys'}`,
			type: 1,
			action: async confirmed => {
				if (confirmed) {
					try {
						const response = await api.post<SubscriptionBuyResponse>('subscription/buy', { sub: currentSub, period: sub.period } as SubscriptionBuyRequest)
						const data = response.data
						if (!data) throw new Error()
						let ntf = lang.Notification_Error

						switch (data.code) {
							case EResponseCode.Success:
								ntf = lang.Notification_Success
								break
							case EResponseCode.NoBalance:
								ntf = lang.SubPageNotification_NoBalance
								break
							case EResponseCode.SubIsBetter:
								ntf = lang.SubPageNotification_SubIsBetter
								break
							case EResponseCode.SubPaused:
								ntf = lang.SubPageNotification_SubPaused
								break
						}

						Notification.ShowNotification(ntf)

						setActive(false)

						if (data.user) {
							setUser(data.user)
						}

						await afterBought()
					} catch (error) {
						if (error instanceof AxiosError) {
							const err = error as AxiosError
							const data = err?.response?.data as SubscriptionBuyResponse

							const message: string = {
								[EResponseCode.NoBalance]: lang.SubPageNotification_NoBalance,
								[EResponseCode.SubIsBetter]: lang.SubPageNotification_SubIsBetter,
							}[data?.code]

							Notification.ShowNotification(message)
						} else {
							Notification.ShowNotification(lang.Notification_Error)
						}
					}
				}
			},
		})
	}

	return (
		<div>
			{(userInfo?.telegram?.isSub || userInfo?.vk?.isSub) != true ? (
				<div className={styles.subSocial}>
					<div className={styles.subWarningTitle}>
						{{
							0: 'Подпишись на наши социальные сети и получи постоянную скидку!',
							1: 'Subscribe to our social networks and get a permanent discount!',
						}[currentLang] || 'Subscribe to our social networks and get a permanent discount!'}
					</div>
					<div>
						{{
							0: 'Подробнее на странице ',
							1: 'More details at ',
						}[currentLang] || 'More details at '}
						<NavLink to='/settings'>
							<u className='btn-simple-hov'>{{ 0: 'настроек', 1: 'settings' }[currentLang] || 'settings'}</u>
						</NavLink>
					</div>
				</div>
			) : (
				<div className={styles.subSocial}>
					<div className={styles.subSuccessTitle}>
						{{
							0: 'Скидка по подпискам в соцсетях активна!',
							1: 'Discount by social subscriptions is active!',
						}[currentLang] || 'Discount by social subscriptions is active!'}
					</div>
					<div>
						{{
							0: 'Подробнее на странице ',
							1: 'More details at ',
						}[currentLang] || 'More details at '}
						<NavLink to='/settings'>
							<u className='btn-simple-hov'>{{ 0: 'настроек', 1: 'settings' }[currentLang] || 'settings'}</u>
						</NavLink>
					</div>
				</div>
			)}
			<div className={styles.subContainer}>
				{subs?.map((sub, i) => {
					if (sub.sub === ESubscription.NoSubscription) return
					if(sub.period !== 30) return 
					
					return (
						<div key={i} className={`${styles.subItem} anim-card`} data-sub={sub.name.toLowerCase()} data-active={user.access === sub.sub}>
							<div className={styles.infoBtn} onClick={() => {
								setCurrentSub(i)
								setActive(true)
							}} title={lang.SubPage_SubInformation}>
								<i className='fa fa-info fa-lg'></i>
							</div>
							{
								{
									[ESubscription.BaseSubscription]: (
										<React.Fragment>
											<svg
												xmlns='http://www.w3.org/2000/svg'
												viewBox='0 0 143.54 99.74'
												className='anim-card-target'
												style={{
													width: '78px',
												}}
											>
												<path
													className='a'
													d='M136.23,1.87c1.3.34,2.71.63,4.07,1.1A2.94,2.94,0,0,1,142,4.15c1.71,3.63,1.74,7.52,1.4,11.41-.28,3.3-.82,6.58-1.08,9.88-.29,3.72-.73,4.68-4.42,5.07-6.81.73-13.66,1.15-20.5,1.57-5.91.36-11.83.52-17.75.8-4.4.21-5.73,1.45-5.49,5.73A76.86,76.86,0,0,0,95.47,49c.42,2.09-.54,2.94-2.15,3.27a29.67,29.67,0,0,1-5.41.5c-5.54.11-11.08.09-16.62.23a19.15,19.15,0,0,1-9.17-1.58c-3.39-1.66-5.79-.65-7.39,2.76-1.29,2.75-3.62,5.3-2.14,8.81.18.42-.43,1.53-1,1.83-3.74,2.12-4.38,5.62-4.57,9.46a5.76,5.76,0,0,1-1.37,3.3,17.61,17.61,0,0,0-4.37,10.3c-.23,2.43.36,5.05-1.84,7a1,1,0,0,0-.28.77c.77,4.27-2.13,4.1-4.91,4.11-7.26,0-14.52.09-21.78,0-1,0-2.58-.4-2.94-1.1C8.35,96.2,6.19,95.2,4.08,93.94,0,91.52-.71,89.12.58,84.51A64.31,64.31,0,0,1,11.05,64C18,54.43,21.28,43.6,22.62,32.07c.6-5.2-2-8.36-7.15-9.13-4.47-.67-4.36-.66-3.92-5.41A96.26,96.26,0,0,0,12,6.66c-.06-2.79.75-4.15,3.74-3.79a2.75,2.75,0,0,0,2.16-.81c2.42-2.89,2.85-2.87,4.51.87H49.13q41.55,0,83.1-.05A16.09,16.09,0,0,0,136.23,1.87ZM62.77,37.38l-1-.13A19.94,19.94,0,0,0,61,41.88c.21,3.22,2.83,5.74,7.16,6.2a100.17,100.17,0,0,0,14,.25c6.27-.2,8.17-3,7.85-9.69-.19-4-2.86-6.48-6.84-6.63-2.19-.09-4.39,0-6.58.14-4.86.26-8.3,2.05-6.59,6.66.54,1.43,2.07,3.8,3.53,4.42,1.26.53,3.81.88,3.71,2.1-.08.92-1.24,1.66-2.2,1.72-3.25.23-6.46-2.77-8.11-5.24-1.16-1.75-2-2.15-3.6-2.28Z'
												></path>
											</svg>
											<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 108.83 27.11' className={styles.subText}>
												<path
													className='a'
													d='M0,.45H16.48c4.54,0,9,1.46,9,6.46,0,3.6-1.91,5.52-5.4,6.42v.07c3.64.49,5.89,3.34,5.89,6,0,6.61-5.33,7.29-10.77,7.29H0Zm9.46,10H12c1.76,0,3.68-.52,3.68-2.51S14,5.41,12.2,5.41H9.46Zm0,10.78h2.63c1.84,0,3.83-.68,3.83-2.7,0-2.26-1.88-2.67-3.76-2.67H9.46Z'
												></path>
												<path
													className='a'
													d='M36,.45H49.82l9.35,26.21H48.62L46.89,21H38.41l-1.62,5.67h-10Zm3.82,14.23h5.82l-2.85-8.6h-.07Z'
												></path>
												<path
													className='a'
													d='M81.69,7.62a23.67,23.67,0,0,0-7.77-1.31c-1.58,0-4.32,0-4.32,1.72,0,3.42,13.78.19,13.78,10.29,0,7-6.95,8.79-12.76,8.79A45.77,45.77,0,0,1,59.8,25.87v-6.8a24.31,24.31,0,0,0,9,1.73c2.37,0,4.51-.49,4.51-1.8,0-3.53-13.78-.34-13.78-10.51C59.54,1.28,67.05,0,73,0a45,45,0,0,1,8.67.9Z'
												></path>
												<path className='a' d='M85.86.45h22.71V6.76H95.69v3.71H108v6.31H95.69v3.57h13.14v6.31h-23Z'></path>
											</svg>
										</React.Fragment>
									),
									[ESubscription.ProSubscription]: (
										<React.Fragment>
											<svg
												xmlns='http://www.w3.org/2000/svg'
												viewBox='0 0 384.31 110.24'
												className='anim-card-target'
												style={{ width: '170px' }}
											>
												<path
													className='a'
													d='M234.56,5.77a10.73,10.73,0,0,1,2.62-.4q19.66.36,39.33.8c2.92.06,5.85.23,8.77.36a1,1,0,0,1,.67.25c1,1.58,2.71,1.82,4.16,1.86,7.76.2,15.53.18,23.29.23,2,0,3.92,0,5.88,0a1.51,1.51,0,0,1,1.48.86c1.26,1.88,2.55,3.75,3.94,5.53,1.2,1.53,3,2.26,4.67,3.16.66.36,1.39.64,1.19,1.68h1.13c6,0,12,.05,18,0,2,0,4-.37,6.09-.59,0-.83,0-1.71,0-2.59a3.19,3.19,0,0,1,.07-.93c1.47-4.7,2.93-9.39,4.45-14.07.53-1.61.95-1.82,2.63-1.82a4.91,4.91,0,0,0,.94,0c.91-.18,1.36.29,1.6,1.07a3.88,3.88,0,0,1,.17,1q0,8.1,0,16.2v1.39l.91-.47c.15-.08.29-.23.44-.23,2.11,0,4.21,0,6.41,0a10.59,10.59,0,0,1,0,1.61c-.17,1.07.29,1.46,1.33,1.61,2.87.44,5.73,1,8.59,1.5.33.06.87.33.9.57a18.32,18.32,0,0,1,0,2.45,1.54,1.54,0,0,1-.31.12H367.2a1.24,1.24,0,0,1-.54-.11c-.39-.18-.76-.4-1.11-.58-.26.43-.47.91-.54.89-1.16-.31-2.62.9-3.53-.66a.36.36,0,0,0-.26-.09h-4.69c.17.86.44,1.27,1.27,1.22,2.1-.13,4.21-.2,6.31-.26a1.33,1.33,0,0,1,1.12,2.06.65.65,0,0,1-.45.27c-1.29,0-2.59,0-3.88,0s-2.81,0-4.24,0l-.15.86h-4.09l-.23-.89-21.58.33-.2,1H326.3l-.2-.94-39.89.61c0,.9,0,1.66,0,2.41,0,.59-.26.93-.93.93s-1.63.1-2.45.12l-17.05.48c-11.09.29-22.17.6-33.26.86a7.81,7.81,0,0,0-4.17,1.33,16.75,16.75,0,0,1-7.93,2.53c-2,.15-5.07-1.7-5.38-4-.76-.38-1.45-1-2.11-1-2.76.1-5.51.4-8.26.62a8.28,8.28,0,0,1-1.22,0c-.65,0-1.3-.19-1.95-.17-1.77,0-3.54.12-5.3.21-1,0-2.06.12-3.1.19,0,.42,0,.74,0,1a49.27,49.27,0,0,0,4.46,17.19,67.44,67.44,0,0,0,8.58,14.39,74.43,74.43,0,0,0,20.41,18.82c.65.39,1.24,1.11,2.26.76l-14,22.85-.14-.29c-.52,0-1.05.08-1.57.17s-1.18.41-1.88,0a89.65,89.65,0,0,1-14.1-10.68,109,109,0,0,1-23-30.19,83.91,83.91,0,0,1-8.5-26.83c-.1-.72-.22-1.44-.33-2.16l-.4,0c-.4,2.36-.81,4.7-1.2,7.06s-.78,4.89-1.17,7.34c-1.78-.18-1.79-.19-1.55-1.72.09-.59.2-1.18.3-1.77a1.59,1.59,0,0,0-.81-1.76,14.8,14.8,0,0,0-1.51-.49,8.62,8.62,0,0,1-1.07-.6L158,48h-1.16c-.21,1.8-.85,2.37-2.79,2.46-4.74.21-9.47.37-14.21.6a5.72,5.72,0,0,1-5.36-2.34,4.27,4.27,0,0,1-3.11,2.46A5.4,5.4,0,0,0,127.67,55q-3.12,9.37-6.34,18.71a3.22,3.22,0,0,0,.34,3,3.41,3.41,0,0,1,.15,3.67,2.18,2.18,0,0,1-2.46,1.14c-3.89-.84-7.78-1.67-11.65-2.56a2.33,2.33,0,0,1-1.3-.91,5.85,5.85,0,0,1-.55-4.93c1.26-4.42,2.54-8.84,3.81-13.26.94-3.31,1.91-6.61,2.78-9.93a3.69,3.69,0,0,0-.92-3.94,3.55,3.55,0,0,1-1.09-2.82c0-.8.16-1.63-.56-2.28-.14-.13-.12-.48-.13-.72,0-.49,0-1,0-1.43a1.63,1.63,0,0,0-.32-.11c-1.24-.14-2.55.54-3.75-.28-.08-.06-.29.06-.43.12a11.39,11.39,0,0,1-1.53.66C93,42.13,82.24,44.89,71.56,47.93c-11.2,3.19-22.31,6.68-33.48,10-3.41,1-6.89,1.75-10.34,2.65a2.59,2.59,0,0,0-1.32.63c-1,1.3-2.68,1.41-4,2.21-.13.08-.43-.1-.64-.16.1-.2.16-.48.33-.59a6.41,6.41,0,0,1,1-.49.46.46,0,0,0-.42-.1c-4.19,1.13-8.35,2.31-12.56,3.34a30.25,30.25,0,0,1-4.58.47c-.15,0-.32-.13-.47-.21s-.52-.37-.78-.37c-1.33,0-1.6-.18-1.71-1.5-.27-3.21-.59-6.42-.81-9.63q-.95-14-1.82-28c-.07-1,.29-1.38,1.36-1.48.28,0,.77-.09.78-.17.09-.87.74-.59,1.21-.62.88-.06,1.77-.12,2.65-.1,8.34.15,16.67.29,25,.48,10.36.24,20.72.53,31.07.8a3.78,3.78,0,0,1,1.21.19,24.46,24.46,0,0,0,12.24.79c2.36-.38,4.68-1.1,7-1.36,5.65-.62,11.32-1.06,17-1.57l6.38-.59c-.56-2.17,1.2-3.12,2.11-4.6a8.43,8.43,0,0,1-1.2-.07.83.83,0,0,1-.62-1.28c.74-1.4,1.56-2.67,3.49-2.12a3.69,3.69,0,0,0,.47,0,1.36,1.36,0,0,0,1.36-.64c1.07-1.36,2.24-2.65,3.3-4a2.1,2.1,0,0,1,1.84-.89q9.1,0,18.2,0a14.72,14.72,0,0,0,1.65-.41,4.1,4.1,0,0,1,1.26.05,7.67,7.67,0,0,0,1.28.36q11.9,0,23.79,0c.85,0,1.76.27,2.5-.48.13-.14.48-.08.73-.06.63,0,1.25.15,1.87.15q13.92,0,27.82,0h8.67C204.61,7,204.61,7,206,6.93c4.25-.23,8.51-.43,12.77-.65.31,0,.61-.09.92-.13-3.18-.2-6.34-.23-9.5-.18-1.07,0-2.15.25-3.23.38a1.78,1.78,0,0,1-.65,0c-1.4-.34-2.78-.76-3.56-2.13-.23-.42-.39-.88-.64-1.45h1.17c.28,1.56,1.16,1.88,2.33.9a1.45,1.45,0,0,1,2.06.06,1.72,1.72,0,0,0,1.49.57c4.77,0,9.54,0,14.31,0a2.32,2.32,0,0,0,1.22-.46c.69-.51,1.44-.62,2,0a2.83,2.83,0,0,0,2.76,1c.54,0,1.08,0,1.61,0a2.6,2.6,0,0,1,2.85,1.72C234.12,6.24,234.3,5.83,234.56,5.77ZM135,47.7c0,1.22,1.12,1.86,2.15,2.27a5.78,5.78,0,0,0,2.22.36c5-.18,9.91-.4,14.86-.62,1.39-.07,1.79-.47,1.81-1.85q0-5,0-9.94c0-1-.21-1.22-1.26-1.18-3.66.15-7.32.33-11,.45-2.11.07-2.76,1.11-3,2.87-.49,3.36.57,6,3.63,7.74a1.13,1.13,0,0,1,.39.27,3.64,3.64,0,0,1,.27.6c-.23,0-.48.17-.66.1-3.16-1.16-5.66-3.84-5.69-7.29a4.08,4.08,0,0,0-2.7-3.88c-.17-.08-.42,0-.87,0C135.1,41,135,44.35,135,47.7Zm99.63-30.8c1.33,0,2.66,0,4,0a.94.94,0,0,1,1,.58,2,2,0,0,0,2,1.14H276a2.59,2.59,0,0,0,2.18-1.13,1.36,1.36,0,0,1,1.27-.59c2.21,0,4.41,0,6.67,0l.16,1.26h3l.11,1.83H319c.27-.6.18-1.06-.52-1.32a3.87,3.87,0,0,1-.93-.47c-2.75-1.9-5.8-2.24-9.08-2.13-6.11.2-12.24.12-18.36.22a15.19,15.19,0,0,0-2.7.54c-.14,0-.35.14-.42.09-1.18-.86-2.51-.36-3.77-.45-1-.08-2.09.09-3.12,0a84,84,0,0,0-14.7-.23,80.6,80.6,0,0,1-11.09.16c-4.59-.39-9.17-.1-13.75-.22-1.76-.05-3.53.21-5.3.3a5.8,5.8,0,0,1-1.16-.17l-.19.23C234.13,16.65,234.37,16.89,234.61,16.9Zm56.61,11.61L324.76,28c1.35,0,1.49-.17,1.42-1.66-.26,0-.53-.05-.8-.05-1.33,0-2.67.11-4,0-10.25-1.05-20.52-.22-30.77-.47-.51,0-1.12-.25-1.47.49-.07.16-.7.09-1.08.09-1.77,0-2,.31-1.75,2.12C288,28.52,289.61,28.53,291.22,28.51Zm39.29-2.67c0,.4,0,.76,0,1.12,0,.66.34,1,1,.95l19.78-.28c.83,0,1.11-.59.88-1.79Zm30.42-13.93c-3,0-2.44-.32-3.26,2.71a1.11,1.11,0,0,0,1.16,1.52c1.29,0,2.59.05,3.89,0a1.62,1.62,0,0,0,1.65-1.71c0-2.6.19-2.58-2.4-2.53h-1Z'
												></path>
											</svg>
											<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 84.72 26.37' className={styles.subText}>
												<path
													className='a'
													d='M0,.44H16.84c4,0,9.34,1.9,9.34,8.76,0,6.65-4.27,9.39-9.38,9.39h-7v7.34H0ZM9.53,12.67h2.16c2.33,0,4.71-.44,4.71-3.29s-2.38-3-4.75-3H9.53Z'
												></path>
												<path
													className='a'
													d='M28.74.44H44.55c6.1,0,9.86,1.9,9.86,6.79,0,3.91-2.45,5.88-6.1,6.76v.07a6.11,6.11,0,0,1,4.31,2.74c1.5,2.7,1.39,6.64,2.41,9.13H44.84c-.44-1.83-.55-5.81-1.35-7.34-.58-1.17-1.57-1.32-2.77-1.32h-2.2v8.66H28.74Zm9.35,11.14h2.55c2,0,4-.22,4-2.82,0-2.3-1.86-2.63-3.83-2.63h-2.7Z'
												></path>
												<path
													className='a'
													d='M70.48,26.37c-8.92,0-14.14-4.5-14.14-13.19C56.34,4.64,61.56,0,70.48,0S84.72,4.64,84.72,13.18C84.72,21.87,79.39,26.37,70.48,26.37Zm0-6.14c3.87,0,4-4.31,4-7,0-2.37-.29-7-4-7s-3.91,4.68-3.91,7.05C66.57,15.92,67,20.23,70.48,20.23Z'
												></path>
											</svg>
										</React.Fragment>
									),
									[ESubscription.ApiSubscription]: (
										<React.Fragment>
											<svg 
												xmlns="http://www.w3.org/2000/svg" 
												viewBox="0 0 512 512" 
												className='anim-card-target-svg'
												style={{ width: '170px' }}>
													<path 
														fillRule="evenodd"
														d="m286.753 335.489-7.402 7.403 21.997 22.05-21.998 22.052 7.403 7.4 29.453-29.453-29.453-29.452zm-68.984 0-29.453 29.452 29.453 29.453 7.402-7.4-21.997-22.053 21.997-22.05-7.402-7.402zm41.894-.892-24.622 59.431 9.712 4.04 24.57-59.428zm92.611-47.934A13.335 13.335 0 0 1 365.61 300v130.83h40.005v6.668a13.386 13.386 0 0 1-13.335 13.334H112.454a13.386 13.386 0 0 1-13.336-13.334v-6.668h40.163V300a13.333 13.333 0 0 1 13.335-13.334h199.5c.053-.003.105-.003.158-.003zm-153.038-69.3a99.007 99.007 0 0 0 106.837 0c3.15.578 6.3 1.156 9.292 1.84a217.668 217.668 0 0 1 74.026 32.498 44.724 44.724 0 0 1 15.224 28.14l15.75 93.502a14.913 14.913 0 0 1-6.51 13.65l-37.904 23.783V300a23.835 23.835 0 0 0-23.836-23.834h-199.5c-.052-.003-.105-.003-.157-.003A23.839 23.839 0 0 0 128.623 300v113.661l-35.962-23.727a21.685 21.685 0 0 1-8.296-19.845l15.75-90.248c1.996-11.027 5.88-22.156 15.226-28.14a236.278 236.278 0 0 1 74.444-32.604zm53.13-168.05a84 84 0 1 1-84 84 84 84 0 0 1 84-84z"  

														/>
											</svg>
											<svg 
												xmlns="http://www.w3.org/2000/svg" 
												viewBox='0 0 84.72 26.37' 
												className={styles.subText}>
												<path 
													className="a" 
													d="M97.07,104.39h13.65l9.21,25.83H109.54l-1.71-5.59H99.47l-1.59,5.59H88Zm3.77,14h5.74l-2.82-8.48h-.07Z" 
												transform="translate(-88 -104.39)"/>
												<path 
													className="a" 
													d="M121.23,104.39h17c4,0,9.48,1.93,9.48,8.88,0,6.74-4.33,9.51-9.51,9.51h-7.11v7.44h-9.91Zm9.65,12.4h2.19c2.37,0,4.77-.44,4.77-3.33,0-2.74-2.4-3.07-4.81-3.07h-2.15Z" 
													transform="translate(-88 -104.39)"/>
												<path className="a" 
													d="M160.63,104.39v25.83h-9.92V104.39Z" 
													transform="translate(-88 -104.39)"/>
											</svg>
										</React.Fragment>
									),
								}[sub.sub]
							}
							<DropdownMenu 
								currIndex={i}
								subs={subs} 
								accessType={sub.sub} 
								onSubChange={(sub) => handleSubChange(i, sub)} 
								selectedSub={selectedSubs[i]} 
								onSubscribe={buySub}
								setActive={handleSetActiveInfo}
							/>
						</div>
						
					)
				})}
			</div>
			
			<SubInfo sub={subs && subs[currentSub]} onBought={afterBought} active={active} setActive={setActive} />			
		</div>
	)
}

export default SubPage
